import "@testing-library/jest-dom";
import { fireEvent, render, screen, within } from "@testing-library/react";
import React from "react";
import LlmRouteProvenanceChip from "./LlmRouteProvenanceChip";
import type { LlmRouteProvenance } from "./llmRouteProvenanceTypes";
import realBackendCapture from "./__fixtures__/f3-real-backend-provenance-msg219476.json";

const sampleProvenance: LlmRouteProvenance = {
  schema_version: 1,
  operation_key: "agent.chat.stream.v1",
  route_revision: "rev-abc123",
  selected_profile: {
    profile_id: "anthropic.claude-haiku-4-5.agent-summary.v1",
    provider: "anthropic",
    model: "claude-haiku-4-5",
    model_version: "claude-haiku-4-5",
    deployment: null,
  },
  runtime_transition_count: 0,
  terminal_failure_kind: null,
  attempts: [
    {
      profile_id: "anthropic.claude-haiku-4-5.agent-summary.v1",
      provider: "anthropic",
      model: "claude-haiku-4-5",
      status: "ok",
      entry_reason: "primary_selection",
      error_reason: null,
      error_class: null,
      capacity: {
        decision: "admitted",
        estimated_input_tokens: 100,
        estimator: "anthropic_local",
        estimator_version: "1",
        requested_output_tokens: 500,
        reserved_output_tokens: 500,
        safety_margin_tokens: 50,
        required_context_tokens: null,
        input_context_tokens: 100,
      },
      duration_ms: 1200,
    },
  ],
  observed: {
    model_identity: {
      profile_id: "anthropic.claude-haiku-4-5.agent-summary.v1",
      provider: "anthropic",
      model: "claude-haiku-4-5",
    },
    observed_model: "claude-haiku-4-5-20251001",
    finish_reason: "stop",
  },
};

// A provenance with a capacity-driven fallback across two attempts.
const fallbackProvenance: LlmRouteProvenance = {
  ...sampleProvenance,
  runtime_transition_count: 1,
  attempts: [
    {
      ...sampleProvenance.attempts[0],
      status: "failed",
      error_reason: "capacity_exhausted",
      error_class: "CapacityExhaustedError",
      capacity: { ...sampleProvenance.attempts[0].capacity, decision: "skipped_capacity" },
      duration_ms: 3,
    },
    {
      profile_id: "anthropic.claude-sonnet-5.long-form-pptx-gen.v1",
      provider: "anthropic",
      model: "claude-sonnet-5",
      status: "ok",
      entry_reason: "permitted_fallback",
      error_reason: null,
      error_class: null,
      capacity: { ...sampleProvenance.attempts[0].capacity, decision: "admitted" },
      duration_ms: 1500,
    },
  ],
};

test("renders the collapsed selected-model chip", () => {
  render(<LlmRouteProvenanceChip provenance={sampleProvenance} />);
  const selected = screen.getByTestId("llm-provenance-selected-model");
  // The chip displays the backend-authored selected model verbatim.
  expect(selected).toHaveTextContent("claude-haiku-4-5");
});

test("expands to show the backend-authored attempt sequence verbatim", () => {
  render(<LlmRouteProvenanceChip provenance={sampleProvenance} />);

  fireEvent.click(screen.getByTestId("llm-provenance-toggle"));

  const toggle = screen.getByTestId("llm-provenance-toggle");
  const panel = screen.getByTestId("llm-provenance-panel");
  expect(toggle).toHaveAttribute("aria-expanded", "true");
  expect(toggle).toHaveAttribute("aria-controls", panel.id);

  // Every rendered value equals the payload field (display, not computation).
  expect(screen.getByText("agent.chat.stream.v1")).toBeInTheDocument();
  expect(screen.getByTestId("llm-provenance-selected-detail")).toHaveTextContent(
    "anthropic/claude-haiku-4-5",
  );
  expect(screen.getByTestId("llm-provenance-observed-model")).toHaveTextContent(
    "claude-haiku-4-5-20251001",
  );
  expect(screen.getByText("rev-abc123")).toBeInTheDocument();
  expect(
    screen.getByTestId("llm-provenance-attempt-0-model"),
  ).toHaveTextContent("anthropic/claude-haiku-4-5");
  expect(
    screen.getByTestId("llm-provenance-attempt-0-status"),
  ).toHaveTextContent("ok");
  expect(screen.getByText("primary_selection")).toBeInTheDocument();
  expect(screen.getByText("admitted")).toBeInTheDocument();
});

test("renders the fallback attempt sequence with the fallback reason", () => {
  render(<LlmRouteProvenanceChip provenance={fallbackProvenance} />);
  fireEvent.click(screen.getByTestId("llm-provenance-toggle"));

  expect(screen.getByTestId("llm-provenance-attempt-0-status")).toHaveTextContent(
    "failed",
  );
  expect(screen.getByText("capacity_exhausted")).toBeInTheDocument();
  expect(screen.getByTestId("llm-provenance-attempt-1-model")).toHaveTextContent(
    "anthropic/claude-sonnet-5",
  );
  expect(screen.getByText("permitted_fallback")).toBeInTheDocument();
});

test("collapsing hides the detail panel", () => {
  render(<LlmRouteProvenanceChip provenance={sampleProvenance} />);
  const toggle = screen.getByTestId("llm-provenance-toggle");
  fireEvent.click(toggle);
  expect(screen.getByTestId("llm-provenance-panel")).toBeInTheDocument();
  fireEvent.click(toggle);
  expect(screen.queryByTestId("llm-provenance-panel")).not.toBeInTheDocument();
  expect(toggle).toHaveAttribute("aria-expanded", "false");
});

test("renders nothing when provenance is absent", () => {
  const { container } = render(<LlmRouteProvenanceChip provenance={undefined} />);
  expect(container).toBeEmptyDOMElement();
  expect(screen.queryByTestId("llm-provenance-card")).not.toBeInTheDocument();
});

// ---------------------------------------------------------------------------
// F3 real-adapter E2E evidence (independently reproducible).
//
// Feeds the EXACT backend-authored `_ida_llm_route_provenance` payload captured
// from the live docker backend during the 2026-07-22 F3 browser E2E
// (conversation 6253, message 219476, backend version-confirmed serving
// e11d59309 with real registered adapters) through the chip and asserts the
// rendered DOM equals the payload field-by-field. This is the committed,
// independently-executable proof of the F3 property (the FE renders
// backend-authored provenance verbatim, computing/fabricating nothing) — it
// replaces the human-attested manual E2E with a test any auditor can run
// without prod access. It goes RED if the UI fabricates, drops, or hardcodes
// any provenance field.
// ---------------------------------------------------------------------------
describe("F3 real-adapter E2E evidence (msg 219476, backend-authored)", () => {
  const provenance = realBackendCapture[
    "_ida_llm_route_provenance"
  ] as unknown as LlmRouteProvenance;

  test("renders the captured backend payload VERBATIM (DOM == payload)", () => {
    render(<LlmRouteProvenanceChip provenance={provenance} />);

    // Collapsed chip = selected_profile.model, verbatim from the payload.
    expect(screen.getByTestId("llm-provenance-selected-model")).toHaveTextContent(
      provenance.selected_profile!.model,
    );

    fireEvent.click(screen.getByTestId("llm-provenance-toggle"));
    const panel = screen.getByTestId("llm-provenance-panel");

    // Every rendered field equals the corresponding backend payload field.
    expect(within(panel).getByText(provenance.operation_key)).toBeInTheDocument();
    expect(screen.getByTestId("llm-provenance-selected-detail")).toHaveTextContent(
      `${provenance.selected_profile!.provider}/${provenance.selected_profile!.model}`,
    );
    expect(screen.getByTestId("llm-provenance-observed-model")).toHaveTextContent(
      provenance.observed!.observed_model,
    );
    expect(
      within(panel).getByText(provenance.observed!.finish_reason as string),
    ).toBeInTheDocument();
    expect(within(panel).getByText(provenance.route_revision)).toBeInTheDocument();

    const attempt = provenance.attempts[0];
    expect(screen.getByTestId("llm-provenance-attempt-0-model")).toHaveTextContent(
      `${attempt.provider}/${attempt.model}`,
    );
    expect(screen.getByTestId("llm-provenance-attempt-0-status")).toHaveTextContent(
      attempt.status,
    );
    expect(within(panel).getByText(attempt.capacity.decision)).toBeInTheDocument();
    expect(within(panel).getByText(`${attempt.duration_ms} ms`)).toBeInTheDocument();
  });

  test("does not hardcode: a different payload model renders differently (anti-fabrication)", () => {
    // If the chip fabricated or hardcoded the model, a mutated payload would not
    // change the DOM. Proves the chip DISPLAYS the payload, not a fixed value.
    const mutated = {
      ...provenance,
      selected_profile: {
        ...provenance.selected_profile!,
        model: "FABRICATION-SENTINEL-XYZ",
      },
    } as LlmRouteProvenance;
    render(<LlmRouteProvenanceChip provenance={mutated} />);
    expect(screen.getByTestId("llm-provenance-selected-model")).toHaveTextContent(
      "FABRICATION-SENTINEL-XYZ",
    );
  });
});
